import { useCallback, useEffect } from 'react';

export default function useStoreCategoryNavigation({ enabled, sectionsKey, navRef, productsRef, selectedCategory, setSelectedCategory }) {
  useEffect(() => {
    if (!enabled) return;
    let frame;
    const update = () => {
      frame = undefined;
      const sections = Array.from(productsRef.current?.querySelectorAll('[data-store-category]') || []);
      const line = (navRef.current?.getBoundingClientRect().bottom || 120) + 16;
      const visible = sections.map(section => ({ section, rect: section.getBoundingClientRect() }));
      let active = visible.find(({ rect }) => rect.top <= line && rect.bottom > line);
      if (!active) active = visible.find(({ rect }) => rect.top > line && rect.top < window.innerHeight);
      const atBottom = window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2;
      if (atBottom) active = visible.filter(({ rect }) => rect.bottom > line && rect.top < window.innerHeight).pop() || active;
      setSelectedCategory(active?.section.dataset.storeCategory || 'All');
    };
    const schedule = () => { if (frame === undefined) frame = window.requestAnimationFrame(update); };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    if (productsRef.current) observer?.observe(productsRef.current);
    if (navRef.current) observer?.observe(navRef.current);
    schedule();
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
      if (frame !== undefined) window.cancelAnimationFrame(frame);
    };
  }, [enabled, sectionsKey, navRef, productsRef, setSelectedCategory]);

  useEffect(() => {
    if (!enabled) return;
    const list = navRef.current?.querySelector('[data-category-filters]');
    const active = Array.from(list?.querySelectorAll('[data-category-filter]') || []).find(button => button.dataset.categoryFilter === selectedCategory);
    if (!list || !active) return;
    const container = list.getBoundingClientRect();
    const button = active.getBoundingClientRect();
    if (button.left < container.left || button.right > container.right) {
      list.scrollBy({ left: button.left + button.width / 2 - container.left - container.width / 2, behavior: 'smooth' });
    }
  }, [enabled, selectedCategory, sectionsKey, navRef]);

  return useCallback(category => {
    setSelectedCategory(category);
    if (category === 'All') { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    const section = Array.from(productsRef.current?.querySelectorAll('[data-store-category]') || []).find(item => item.dataset.storeCategory === category);
    if (!section) return;
    const nav = navRef.current;
    const stickyTop = nav ? parseFloat(window.getComputedStyle(nav).top) || 0 : 56;
    const offset = stickyTop + (nav?.getBoundingClientRect().height || 64) + 16;
    window.scrollTo({ top: Math.max(0, window.scrollY + section.getBoundingClientRect().top - offset), behavior: 'smooth' });
  }, [navRef, productsRef, setSelectedCategory]);
}
